// src/components/plataforma/contas/ContaFormPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccountById, useMutateAccounts } from '../../../hooks/useAccounts';
import { Loader2, Save, Building, Phone } from 'lucide-react'; // ✅ Removido: Globe
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';

const ContaFormPage = () => {
    const { franquiaId, contaId } = useParams();
    const navigate = useNavigate();
    const isEditing = !!contaId;

    const { data: contaData, isLoading: pageLoading } = useAccountById(contaId, { enabled: isEditing });
    const { createAccount, updateAccount } = useMutateAccounts();
    const isMutationLoading = createAccount.isPending || updateAccount.isPending;
    
    // Funções de formatação
    const formatCPF = (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
        if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
        return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    };

    const formatTelefone = (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 2) return numbers;
        if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    };

    const formatCNPJ = (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 2) return numbers;
        if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
        if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
        if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
        return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
    };

    const [activeTab, setActiveTab] = useState('info');
    const [formData, setFormData] = useState({
        name: '',
        nomeFantasia: '',
        email: '',
        telefone: '',
        cnpj: '',
        status: 'trialing',
        redesSociais: { instagram: '', facebook: '', tiktok: '', youtube: '' },
        owner: { name: '', email: '', password: '', telefone: '', cpf: '' }
    });

    useEffect(() => {
        if (isEditing && contaData) {
            setFormData({
                name: contaData.name || '',
                nomeFantasia: contaData.nomeFantasia || '',
                email: contaData.email || '',
                telefone: formatTelefone(contaData.telefone || ''),
                cnpj: formatCNPJ(contaData.cnpj || ''),
                status: contaData.status || 'active',
                redesSociais: { 
                    instagram: contaData.redesSociais?.instagram || '',
                    facebook: contaData.redesSociais?.facebook || '',
                    tiktok: contaData.redesSociais?.tiktok || '',
                    youtube: contaData.redesSociais?.youtube || '',
                },
                owner: {
                    name: contaData.owner?.name || '',
                    email: contaData.owner?.email || '',
                    cpf: formatCPF(contaData.owner?.cpf || ''),
                    telefone: formatTelefone(contaData.owner?.telefone || ''),
                    password: ''
                }
            });
        }
    }, [contaData, isEditing]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        
        // Aplicar formatação baseada no nome do campo
        let formattedValue = value;
        
        if (name === 'owner.cpf') {
            formattedValue = formatCPF(value);
        } else if (name === 'owner.telefone' || name === 'telefone') {
            formattedValue = formatTelefone(value);
        } else if (name === 'cnpj') {
            formattedValue = formatCNPJ(value);
        } else {
            formattedValue = value; // Para campos sem formatação
        }
        
        // Lidar com campos aninhados
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            
            if (parent === 'redesSociais') {
                setFormData(prev => ({ 
                    ...prev, 
                    redesSociais: { 
                        ...prev.redesSociais, 
                        [child]: formattedValue 
                    } 
                }));
            } else if (parent === 'owner') {
                setFormData(prev => ({ 
                    ...prev, 
                    owner: { 
                        ...prev.owner, 
                        [child]: formattedValue 
                    } 
                }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: formattedValue }));
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Preparar dados para envio (remover formatação)
        const dataToSend = {
            ...formData,
            telefone: formData.telefone.replace(/\D/g, ''),
            cnpj: formData.cnpj.replace(/\D/g, ''),
            owner: {
                ...formData.owner,
                cpf: formData.owner.cpf.replace(/\D/g, ''),
                telefone: formData.owner.telefone.replace(/\D/g, '')
            }
        };
        
        const returnUrl = franquiaId ? `/franquias/${franquiaId}/contas` : '/plataforma/contas';
        const mutationOptions = { onSuccess: () => navigate(returnUrl) };

        if (isEditing) {
            const { owner, ...dadosParaUpdate } = dataToSend;
            updateAccount.mutate({ accountId: contaId, accountData: dadosParaUpdate }, mutationOptions);
        } else {
            createAccount.mutate({ franquiaId, contaData: dataToSend }, mutationOptions);
        }
    };

    if (pageLoading && isEditing) {
        return (
            <div className="page-management-container">
                <div className="p-8 text-center">
                    <Loader2 className="animate-spin mx-auto" size={32} />
                </div>
            </div>
        );
    }

    const returnUrl = franquiaId ? `/franquias/${franquiaId}/contas` : '/plataforma/contas';

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    icon={<Building />}
                    title={isEditing ? `Editar Conta: ${formData.name}` : 'Nova Conta'}
                    navBack={{ to: returnUrl, text: 'Voltar' }}
                />

                <div className="tabs-nav mt-6">
                    <button 
                        type="button" 
                        className={`tab-button ${activeTab === 'info' ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab('info')}
                    >
                        <Building size={16} /> Informações da Conta
                    </button>
                    <button 
                        type="button" 
                        className={`tab-button ${activeTab === 'contato' ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab('contato')}
                    >
                        <Phone size={16} /> Contato e Redes
                    </button>
                    {/* ✅ REMOVIDO: TabButton para Página Pública */}
                </div>

                <div className="content-panel mt-0 pt-8">
                    {activeTab === 'info' && (
                        <fieldset className="form-section" disabled={isMutationLoading}>
                            <legend className="form-section-title">Dados Principais</legend>
                            <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                                <div className="form-group">
                                    <label>Nome da Conta / Empresa*</label>
                                    <input 
                                        type="text" 
                                        name="name" 
                                        value={formData.name} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Nome Fantasia</label>
                                    <input 
                                        type="text" 
                                        name="nomeFantasia" 
                                        value={formData.nomeFantasia} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>CNPJ</label>
                                    <input 
                                        type="text" 
                                        name="cnpj" 
                                        value={formData.cnpj} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        maxLength={18}
                                    />
                                </div>
                                {isEditing && (
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select 
                                            name="status" 
                                            value={formData.status} 
                                            onChange={handleChange} 
                                            className="form-input" 
                                            required
                                        >
                                            <option value="trialing">Em Teste</option>
                                            <option value="active">Ativa</option>
                                            <option value="suspended">Suspensa</option>
                                            <option value="canceled">Cancelada</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </fieldset>
                    )}
                    
                    {activeTab === 'contato' && (
                        <fieldset className="form-section" disabled={isMutationLoading}>
                            <legend className="form-section-title">Informações de Contato</legend>
                            <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                                <div className="form-group">
                                    <label>Email de Contato</label>
                                    <input 
                                        type="email" 
                                        name="email" 
                                        value={formData.email} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Telefone de Contato</label>
                                    <input 
                                        type="tel" 
                                        name="telefone" 
                                        value={formData.telefone} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        maxLength={15}
                                    />
                                </div>
                            </div>
                            <legend className="form-section-title mt-6">Redes Sociais</legend>
                            <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                                <div className="form-group">
                                    <label>Instagram</label>
                                    <input 
                                        type="text" 
                                        name="redesSociais.instagram" 
                                        value={formData.redesSociais.instagram} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        placeholder="ex: @seu-usuario" 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Facebook</label>
                                    <input 
                                        type="text" 
                                        name="redesSociais.facebook" 
                                        value={formData.redesSociais.facebook} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        placeholder="ex: /sua-pagina" 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>TikTok</label>
                                    <input 
                                        type="text" 
                                        name="redesSociais.tiktok" 
                                        value={formData.redesSociais.tiktok} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Youtube</label>
                                    <input 
                                        type="text" 
                                        name="redesSociais.youtube" 
                                        value={formData.redesSociais.youtube} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                    />
                                </div>
                            </div>
                        </fieldset>
                    )}
                    
                    {/* ✅ REMOVIDO: Seção da Página Pública */}
                    
                    {!isEditing && (
                        <fieldset className="form-section" disabled={isMutationLoading}>
                            <legend className="form-section-title">Dados do Proprietário da Conta</legend>
                            <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                                <div className="form-group">
                                    <label>Nome do Proprietário*</label>
                                    <input 
                                        type="text" 
                                        name="owner.name" 
                                        value={formData.owner.name} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email do Proprietário*</label>
                                    <input 
                                        type="email" 
                                        name="owner.email" 
                                        value={formData.owner.email} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Telefone*</label>
                                    <input 
                                        type="tel" 
                                        name="owner.telefone" 
                                        value={formData.owner.telefone} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        required 
                                        placeholder="(XX) XXXXX-XXXX" 
                                        maxLength={15}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>CPF do Proprietário*</label>
                                    <input 
                                        type="text" 
                                        name="owner.cpf" 
                                        value={formData.owner.cpf} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        required 
                                        placeholder="000.000.000-00" 
                                        maxLength={14}
                                    />
                                </div>
                                <div className="form-group full-width md:col-span-2">
                                    <label>Senha Inicial*</label>
                                    <input 
                                        type="password" 
                                        name="owner.password" 
                                        value={formData.owner.password} 
                                        onChange={handleChange} 
                                        className="form-input" 
                                        required 
                                    />
                                </div>
                            </div>
                        </fieldset>
                    )}
                </div>

                <FabSaveButton loading={isMutationLoading} label={isEditing ? 'Salvar Alterações' : 'Criar Conta'} />
            </form>
        </div>
    );
};

export default ContaFormPage;